import * as z from "zod";

import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import { useTranslation } from "react-i18next";
import GenericButton from "widgets/controls/buttons/generic";
import ComboBox from "widgets/shared/inputs/combobox";
import TimePicker from "widgets/shared/inputs/timePicker";
import i18next from "i18next";
import InputField from "widgets/shared/inputs/inputField";
import { MdAdd, MdClose } from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";
import {
  convertLocalTimeToUTCWithDayShift,
  shiftDowByDayDelta,
  shiftDomByDayDelta,
} from "util/datelib";

// Schema
const ScheduleSchema = z.object({
  description: z.string().min(1, { message: i18next.t("zod.error.required") }),
  frequency: z.enum(["DAILY", "WEEKLY", "MONTHLY"], {
    required_error: i18next.t("zod.error.required"),
  }),
  dom: z.number().nullable(),
  dow: z.number().nullable(),
  hourOn: z.object(
    { hour: z.number(), minute: z.number() },
    {
      required_error: i18next.t("zod.error.required"),
    }
  ),
  recipients: z
    .array(
      z.object({
        email: z
          .string()
          .min(1, { message: i18next.t("zod.error.required") })
          .email({ message: i18next.t("zod.error.email") }),
      })
    )
    .default([]),
  emailBody: z.string().optional(),
});

export type ScheduleFormType = z.infer<typeof ScheduleSchema>;

// Props
type ScheduleFormProps = {
  submit: (data: ScheduleFormType) => void;
  onClose?: () => void;
  values?: ScheduleFormType;
  disabled?: boolean;
  context?: "new" | "edit" | "view";
  loading?: boolean;
};

const ScheduleForm = ({
  submit,
  onClose,
  values,
  disabled = false,
  context = "new",
  loading = false,
}: ScheduleFormProps) => {
  const form = useForm<ScheduleFormType>({
    resolver: zodResolver(ScheduleSchema),
    values,
  });
  const { t } = useTranslation();
  const { control } = form;
  const { errors, isDirty } = form.formState;

  const onSubmit = async (formData: ScheduleFormType) => {
    try {
      // Convert local wall-clock time to UTC, and detect if the calendar day changes.
      const utcTime = convertLocalTimeToUTCWithDayShift(
        formData.hourOn.hour,
        formData.hourOn.minute
      );

      const adjustedData: ScheduleFormType = {
        ...formData,
        hourOn: { hour: utcTime.hour, minute: utcTime.minute },
      };

      // If the UTC time lands on a different calendar day, shift weekly/monthly rules
      // so the backend interprets them as UTC-based schedule.
      if (formData.frequency === "WEEKLY" && adjustedData.dow != null) {
        adjustedData.dow = shiftDowByDayDelta(adjustedData.dow, utcTime.dayDelta);
      }

      if (formData.frequency === "MONTHLY" && adjustedData.dom != null) {
        adjustedData.dom = shiftDomByDayDelta(adjustedData.dom, utcTime.dayDelta);
      }

      await submit(adjustedData);
    } catch (error) {
      //   setCustomError(t("form.error.unknown"));
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "recipients",
  });

  const clearErrors = () => {
    form.clearErrors();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={` space-y-4 ${context === "new" ? "-mt-16" : "-mt-4"}`}
      >
        {context === "new" && (
          <div className="flex justify-end gap-2 pb-10">
            <GenericButton
              type="submit"
              text={"Schedule"}
              size="sm"
              variant="outline"
              color="white"
              rounded="rounded-xl"
              disabled={disabled || !form.formState.isDirty}
            />
          </div>
        )}
        {context !== "edit" && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="description"
              rules={{ required: true }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={`${context === "new" ? "text-white" : ""}`}
                  >
                    {t("form.input.description")}
                  </FormLabel>
                  <FormControl>
                    <InputField
                      placeholder={t("form.placeholder.description")}
                      id="schedule-form-name"
                      type="text"
                      state={errors.description ? "error" : undefined}
                      onFocus={clearErrors}
                      sizes="md"
                      color="secondary"
                      variant="solid"
                      disabled={disabled || context !== "new"}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="frequency"
              rules={{ required: true }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={`${context === "new" ? "text-white" : ""}`}
                  >
                    {t("form.input.frequency")}
                  </FormLabel>
                  <FormControl>
                    <ComboBox
                      items={[
                        { label: "Daily", value: "DAILY" },
                        { label: "Weekly", value: "WEEKLY" },
                        { label: "Monthly", value: "MONTHLY" },
                      ]}
                      placeholder={t("form.placeholder.frequency")}
                      id="schedule-form-frequency"
                      state={errors.frequency ? "error" : undefined}
                      onFocus={clearErrors}
                      clearIcon={false}
                      sizes="md"
                      color="secondary"
                      variant="solid"
                      disabled={disabled || context !== "new"}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.watch("frequency") === "MONTHLY" && (
              <FormField
                control={form.control}
                name="dom"
                rules={{ required: true }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel
                      className={`${context === "new" ? "text-white" : ""}`}
                    >
                      {t("form.input.dom")}
                    </FormLabel>
                    <FormControl>
                      <ComboBox
                        items={[
                          { label: "1st", value: 1 },
                          { label: "2nd", value: 2 },
                          { label: "3rd", value: 3 },
                          { label: "4th", value: 4 },
                          { label: "5th", value: 5 },
                          { label: "6th", value: 6 },
                          { label: "7th", value: 7 },
                          { label: "8th", value: 8 },
                          { label: "9th", value: 9 },
                          { label: "10th", value: 10 },
                          { label: "11th", value: 11 },
                          { label: "12th", value: 12 },
                          { label: "13rd", value: 13 },
                          { label: "14th", value: 14 },
                          { label: "15th", value: 15 },
                          { label: "16th", value: 16 },
                          { label: "17th", value: 17 },
                          { label: "18th", value: 18 },
                          { label: "19th", value: 19 },
                          { label: "20th", value: 20 },
                          { label: "21st", value: 21 },
                          { label: "22nd", value: 22 },
                          { label: "23rd", value: 23 },
                          { label: "24th", value: 24 },
                          { label: "25th", value: 25 },
                          { label: "26th", value: 26 },
                          { label: "27th", value: 27 },
                          { label: "28th", value: 28 },
                          { label: "End of Month", value: 32 },
                        ]}
                        placeholder={t("form.placeholder.dom")}
                        id="schedule-form-dom"
                        state={errors.dom ? "error" : undefined}
                        onFocus={clearErrors}
                        clearIcon={false}
                        sizes="md"
                        color="secondary"
                        variant="solid"
                        disabled={disabled || context !== "new"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {form.watch("frequency") === "WEEKLY" && (
              <FormField
                control={form.control}
                name="dow"
                rules={{ required: true }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel
                      className={`${context === "new" ? "text-white" : ""}`}
                    >
                      {t("form.input.dow")}
                    </FormLabel>
                    <FormControl>
                      <ComboBox
                        items={[
                          { label: "Monday", value: 1 },
                          { label: "Tuesday", value: 2 },
                          { label: "Wednesday", value: 3 },
                          { label: "Thursday", value: 4 },
                          { label: "Friday", value: 5 },
                          { label: "Saturday", value: 6 },
                          { label: "Sunday", value: 0 },
                        ]}
                        placeholder={t("form.placeholder.dow")}
                        id="schedule-form-dow"
                        clearIcon={false}
                        sizes="md"
                        color="secondary"
                        variant="solid"
                        state={errors.dow ? "error" : undefined}
                        onFocus={clearErrors}
                        disabled={disabled || context !== "new"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="hourOn"
              rules={{ required: true }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={`${context === "new" ? "text-white" : ""}`}
                  >
                    {t("form.input.hour.on")}
                  </FormLabel>
                  <FormControl>
                    <TimePicker
                      placeholder={t("form.placeholder.hour.on")}
                      id="schedule-form-hour-on"
                      value={{
                        hour: field.value?.hour ?? 0,
                        minute: 0,
                      }}
                      onChange={({ hour, minute }) => {
                        field.onChange({ hour, minute });
                      }}
                      clearIcon={false}
                      sizes="md"
                      color="secondary"
                      variant="solid"
                      hourOnly
                      disabled={disabled || context !== "new"}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {context !== "view" && (
          <>
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`text-txt-lg ${
                    context === "new" ? "text-white" : ""
                  }`}
                >
                  {t("form.input.email")}
                </div>
                <CircleButton
                  // label="Add"
                  icon={MdAdd}
                  onClick={() => {
                    append({ email: "" });
                  }}
                  disabled={disabled}
                  size="xs"
                  variant="solid"
                  color={context === "new" ? "white" : "secondary"}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {fields.map((fieldItem, index) => (
                <FormField
                  key={fieldItem.id}
                  control={form.control}
                  name={`recipients.${index}.email`}
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl className="flex-1">
                        <InputField
                          placeholder={t("form.placeholder.email")}
                          id={`recipients-${index}`}
                          type="text"
                          onFocus={clearErrors}
                          sizes="md"
                          color="secondary"
                          variant="solid"
                          disabled={disabled}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      <CircleButton
                        // label="Add"
                        icon={MdClose}
                        onClick={() => {
                          remove(index);
                        }}
                        disabled={disabled}
                        size="sm"
                        variant="solid"
                        color={context === "new" ? "white" : "secondary"}
                      />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </>
        )}
        {context === "edit" && (
          <div className="flex justify-end pt-2">
            <div className="flex gap-2">
              <GenericButton
                onClick={onClose}
                text={t("button.action.cancel")}
                size="md"
                variant="ghost"
                color="secondary"
              />

              <GenericButton
                type="submit"
                text={t("button.action.ok")}
                disabled={!isDirty}
                loading={loading || false}
                size="md"
              />
            </div>
          </div>
        )}
        {context === "view" && (
          <div className="flex justify-end pt-2">
            <div className="flex gap-2">
              <GenericButton
                onClick={onClose}
                text={t("button.action.ok")}
                disabled={!isDirty}
                loading={loading || false}
                size="md"
              />
            </div>
          </div>
        )}
      </form>
    </Form>
  );
};

export default ScheduleForm;
